import React, { useEffect, useRef, useState } from "react";

import { useGetDataInfoRange, useGetRecentData } from "api/api-hooks/datasets-hooks";
import { Instant } from "@js-joda/core";
import useDefaultPlotlyConfig from "hooks/useDefaultPlotlyConfig";
import { APIResponse } from "services/HttpService";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { ProgressSpinner } from "primereact/progressspinner";
import { SelectButton } from "primereact/selectbutton";
import { Tooltip } from "primereact/tooltip";
import { useResizeDetector } from "react-resize-detector";
import { T } from "services/lang/LanguageService";
import useChartDialogStore from "store/chart-dialog-store";
import { addToInstant, DweenTimeUnitEnum, getDiffSeconds, toInstant, toNativeDate, truncateDate } from "utils/time-utils";
import { getChartSizeValue } from "utils/utils";
import { useTimezone } from "hooks/useTimezone";
import CalendarWrapper from "components/basic/calendar-wrapper/CalendarWrapper";
import CustomToggleButton from "components/basic/custom-toggle-button/CustomToggleButton";
import { dialogIcon } from "../icons";
import { DatasetChartWrapperProps, DatasetLoadingConf, LoadingConfig, PlotDataEx } from "../interfaces";
import {
  defaultFromDateTimeInstant,
  defaultToDateTimeInstant,
  isLagLeadLoadingConfig,
  offsetOptions,
  shouldShowTime,
} from "../utils";
import "./ChartWrapperStyle.scss";

const getDefaultLoadConf = (loadConf?: LoadingConfig): DatasetLoadingConf =>
  isLagLeadLoadingConfig(loadConf)
    ? {
        alignUnit: loadConf.alignUnit ?? undefined,
        lag: loadConf.lag ?? 6,
        lagUnit: loadConf.lagUnit ?? DweenTimeUnitEnum.HOURS,
        lead: loadConf.lead ?? 0,
        leadUnit: loadConf.leadUnit ?? DweenTimeUnitEnum.HOURS,
      }
    : {
        alignUnit: DweenTimeUnitEnum.MINUTES,
        lag: 6,
        lagUnit: DweenTimeUnitEnum.HOURS,
        lead: 0,
        leadUnit: DweenTimeUnitEnum.HOURS,
      };

export default function ChartWrapper<ResT>(props: DatasetChartWrapperProps<ResT>) {
  const tz = useTimezone();

  const [loaded, setLoaded] = useState(!!props.withoutIdentifiers);
  const { width, height, ref } = useResizeDetector();
  const { height: height2, ref: ref2 } = useResizeDetector();
  const [chartDialog, setChartDialog] = useState(false);
  const { setDateFrom, setDateTo, dateFrom, dateTo, isOpen } = useChartDialogStore();
  const defaultConfig = useDefaultPlotlyConfig();

  const defaultFromDate =
    toInstant(props.dateFrom) ||
    toInstant(dateFrom) ||
    defaultFromDateTimeInstant({
      referenceTime: props.instantReferenceTime,
      zoneId: tz.company,
      loadConf: getDefaultLoadConf(props.loadConf),
    });

  const defaultToDate =
    toInstant(props.dateTo) ||
    toInstant(dateTo) ||
    defaultToDateTimeInstant({
      referenceTime: props.instantReferenceTime,
      zoneId: tz.company,
      loadConf: getDefaultLoadConf(props.loadConf),
    });
  const [from, setFrom] = useState<Instant>(defaultFromDate);
  const [to, setTo] = useState<Instant>(defaultToDate);

  useEffect(() => {
    let newFrom;
    let newTo;

    if (props.productItemsDateRange && props.productItemsDateRange.length > 0) {
      newFrom = toInstant(props.productItemsDateRange[0]);
      if (props.productItemsDateRange[1].length > 0) {
        newTo = toInstant(props.productItemsDateRange[1]);
      } else {
        newTo = Instant.now();
      }
    } else {
      newFrom = toInstant(props.dateFrom);
      newTo = toInstant(props.dateTo);
    }

    if (newFrom && newFrom !== from) {
      setFrom(newFrom);
    }

    if (newTo && newTo !== to) {
      setTo(newTo);
    }
  }, [props.dateFrom, props.dateTo, props.productItemsDateRange]);

  useEffect(() => {
    let dticksAmount;
    if (props.timeAxisStepSeconds) {
      dticksAmount = (to.toEpochMilli() - from.toEpochMilli()) / (props.timeAxisStepSeconds * 1000);
      if (dticksAmount <= props.maxDTicks!!) {
        props.setDticks!(props.timeAxisStepSeconds * 1000);
      } else {
        props.setDticks!(undefined);
      }
    }
  }, [from, to]);

  const chartDialogHandler = () => {
    if (props.chartDialogOpen) {
      return;
    }
    setChartDialog(prevState => !prevState);
  };

  const dialogButton = {
    title: T("word.chart-in-dialog"),
    name: "Chart in Dialog",
    icon: dialogIcon,
    click: chartDialogHandler,
  };

  const modeBarButtons = [...(props.chartConfig?.modeBarButtonsToAdd || [])];
  modeBarButtons.unshift(dialogButton);

  const config = { ...defaultConfig, ...props.chartConfig, modeBarButtonsToAdd: modeBarButtons };

  const [selectedTimeOffset, setSelectedTimeOffset] = useState<string | undefined>(undefined);

  const requestingTime = useRef<{
    from: Instant | undefined;
    to: Instant | undefined;
    loading: boolean;
  }>({
    from: undefined,
    to: undefined,
    loading: false,
  });

  const [refresh, setRefresh] = useState<{
    updateFrequency: number;
    turned: boolean;
  }>({
    updateFrequency:
      (props.updateFrequency && !props.reportMode) || props.reportMode === undefined ? (props.updateFrequency ?? 5) : 0,
    turned: !!props.updateFrequency,
  });

  const presentDate = useRef<Date>(undefined);

  const {
    data: recentData,
    isLoading: isRecentDataLoading,
    error: recentDataError,
  } = useGetRecentData(props.identifiers as string[], {
    refTime: to.toString(),
    options: {
      enabled: !!(props.getMostRecent && props.showDateSelection), // makes undefined to false
      staleTime: 1000,
    },
  });

  const {
    data: traces,
    isFetching: isLoading,
    refetch,
    error: dataInfoRangeError,
  } = useGetDataInfoRange(
    props.identifiers as string[],
    {
      uniqueKey: props.uniqueKey,
      timeStart: from.toString(),
      timeEnd: to.toString(),
      downsampleInterval: props.downsampleInterval,
      usePromiseHandler: res => {
        if (props.getMostRecent && props.showDateSelection && recentData) {
          const updatedData = res.data?.map(item => {
            const recentItem = recentData.find(recent => recent.identifier === item.identifier);
            return recentItem ? { ...item, data: recentItem.data } : item;
          });
          res.data = updatedData;
        }
        requestingTime.current.to = Instant.now();
        requestingTime.current.loading = false;

        if (!res.error) {
          return props.promiseHandler(res as APIResponse<ResT>);
        }

        console.error(res.error.message);
        throw new Error(res.error.message);
      },
    },
    {
      enabled:
        (from && to && !props.getMostRecent && !!props.identifiers?.length) ||
        (!isRecentDataLoading && !!recentData && !!props.identifiers?.length),
      staleTime: 1000,
    }
  );

  if (props.statusHandler && (recentDataError || dataInfoRangeError)) {
    props.statusHandler("error");
  }

  useEffect(() => {
    if (traces && !isLoading) {
      syncpresentdate();
      setLoaded(true);
    }
    if (isLoading && !requestingTime.current.loading) {
      requestingTime.current.from = Instant.now();
      requestingTime.current.loading = true;
    }
  }, [traces, isLoading]);

  useEffect(() => {
    if (refresh.turned && refresh.updateFrequency && refresh.updateFrequency > 0 && presentDate && presentDate.current) {
      const interval = setInterval(() => {
        const tempDate = truncateDate(Instant.now(), DweenTimeUnitEnum.MINUTES, tz.company);
        if (
          presentDate &&
          presentDate.current &&
          presentDate.current
            ?.toISOString()
            .localeCompare(truncateDate(Instant.now(), DweenTimeUnitEnum.MINUTES, tz.company).toISOString()) !== 0
        ) {
          const minutesToAdd = getDiffSeconds(tempDate, presentDate.current!) / 60;
          if (minutesToAdd > 0) {
            addTimeToBothDates(minutesToAdd);
            presentDate.current = truncateDate(new Date(), DweenTimeUnitEnum.MINUTES, tz.company);
          }
        } else {
          refetch();
        }
      }, refresh.updateFrequency * 1000);
      return () => clearInterval(interval);
    }
    return undefined;
  }, [refresh.turned, refresh.updateFrequency, presentDate.current]);

  const handleFromDateAccordingToTimeOffset = (timeOffset: string | undefined, changedTimeOffset?: boolean) => {
    setSelectedTimeOffset(timeOffset);

    if (timeOffset) {
      setFrom(addToInstant(to, -1, timeOffset));
    } else if (from && changedTimeOffset) {
      setFrom(
        defaultFromDateTimeInstant({
          zoneId: tz.company,
          loadConf: props.loadConf as DatasetLoadingConf,
          referenceTime: props.instantReferenceTime,
        })
      );
    }
  };

  useEffect(() => {
    if (isOpen) setDateFrom(toNativeDate(from));
  }, [from]);

  const addTimeToBothDates = (minutesToAdd: number) => {
    setFrom(oldFrom => addToInstant(oldFrom, minutesToAdd, DweenTimeUnitEnum.MINUTES));
    setTo(oldTo => addToInstant(oldTo, minutesToAdd, DweenTimeUnitEnum.MINUTES));
  };

  const onRefresh = () => {
    if (from && to && props.identifiers) {
      refetch();
    }
  };

  const setFromDateHandler = (date: Instant) => {
    if (refresh.turned) {
      setRefresh({ ...refresh, turned: false });
    }

    setFrom(date);
    handleFromDateAccordingToTimeOffset(undefined, false);
  };

  const setToDateHandler = (date: Instant) => {
    if (refresh.turned) {
      setRefresh({ ...refresh, turned: false });
    }

    setTo(date);
    if (isOpen) setDateTo(date.toDate());
    handleFromDateAccordingToTimeOffset(undefined, false);
  };

  const onSelectButtonChange = (e: any) => {
    if (refresh.turned) {
      setRefresh({ ...refresh, turned: false });
    }
    handleFromDateAccordingToTimeOffset(e.value, true);
  };

  const syncpresentdate = () => {
    presentDate.current = truncateDate(new Date(), "MINUTES", tz.company);
  };

  const totalDataPoints = () => {
    if (traces) {
      return traces.map((t: any) => t.x?.length).reduce((c: any, a: any) => c! + a!, 0);
    }

    return 0;
  };

  const printRequestingTime = () => {
    if (requestingTime.current.from && requestingTime.current.to) {
      const time = requestingTime.current.to.toEpochMilli() - requestingTime.current.from.toEpochMilli();
      if (time > 1000) {
        return `${(time / 1000).toFixed(1)}s`;
      }
      return `${time.toFixed(0)}ms`;
    }
    return "-";
  };

  const getChartHeight = () => {
    if (props.height != null) {
      return getChartSizeValue(props.height);
    }
    if (height && height2 && height >= 0 && height2 >= 0 && props.dynamicHeight) {
      return height - height2 - 4;
    }
    return 400;
  };

  const getChartWidth = () => {
    if (props.width != null) {
      return getChartSizeValue(props.width);
    }

    if (props.reportMode) {
      return 960;
    }

    return width;
  };

  const handleResetDates = () => {
    setFrom(defaultFromDate);
    setTo(defaultToDate);
    handleFromDateAccordingToTimeOffset(undefined, false);

    onSelectButtonChange;
    if (props.updateFrequency && props.updateFrequency > 0) {
      setRefresh({ ...refresh, turned: true });
    }
  };

  return (
    <div
      ref={ref}
      style={{
        overflow: "hidden",
        height: "100%",
        width: props.reportMode ? (props.width ?? 960) : undefined,
      }}
      id={props.uniqueKey}
    >
      <Tooltip target="#resetIcon" />
      {props.dateAndRefreshButtonsVisible !== false && !props.hideHeader && (
        <div ref={ref2} className="flex align-items-center p-mx-2 justify-content-between non-printable">
          <div className="flex align-items-center p-mx-2 flex-wrap gap-1">
            {props.showDateSelection && (
              <>
                {!props.getMostRecent && (
                  <CalendarWrapper
                    id="from"
                    value={from.toDate()}
                    showTime={shouldShowTime(props.loadConf)}
                    onInstantChange={setFromDateHandler}
                    debounceTimeInMs={300}
                  />
                )}
                <CalendarWrapper
                  id="to"
                  value={to.toDate()}
                  showTime={shouldShowTime(props.loadConf)}
                  onInstantChange={setToDateHandler}
                  debounceTimeInMs={300}
                />
                {!!props.updateFrequency && props.updateFrequency > 0 && !refresh.turned && (
                  <div>
                    <Tooltip target="#resetIcon" />
                    <i
                      className="pi pi-refresh cursor-pointer mr-4"
                      onClick={handleResetDates}
                      data-pr-tooltip="Reset Dates"
                      data-pr-position="top"
                      id="resetIcon"
                    />
                  </div>
                )}
                <div className="flex gap-2 align-items-center">
                  <div style={{ flexWrap: "nowrap", whiteSpace: "nowrap" }}>
                    <SelectButton
                      value={selectedTimeOffset}
                      options={offsetOptions}
                      onChange={onSelectButtonChange}
                      className="select-button-class"
                    />
                  </div>
                  {props.datasetChart !== "pie" && (
                    <span className="flex align-items-center">
                      <Button icon="pi pi-info-circle" className="p-button-secondary p-button-text" disabled />
                      {isLoading ? (
                        <span>
                          <ProgressSpinner
                            style={{ width: "2rem", height: "2rem" }}
                            strokeWidth="4"
                            fill="var(--surface-ground)"
                            animationDuration=".5s"
                          />
                        </span>
                      ) : (
                        <>
                          <Tooltip target=".time-spent-requesting" showDelay={1000} />
                          <span className="time-spent-requesting" data-pr-tooltip={printRequestingTime()}>
                            {totalDataPoints()}
                          </span>
                        </>
                      )}
                    </span>
                  )}
                </div>
              </>
            )}
          </div>
          <div className="flex align-items-center gap-1">
            {!props.hideRefresh && !props.reportMode && (
              <div>
                <Button
                  icon="pi pi-refresh"
                  type="button"
                  loading={isLoading}
                  onClick={onRefresh}
                  className="component-editor-button icon-blue"
                />
              </div>
            )}
            {props.updateFrequency && props.updateFrequency > 0 ? (
              <CustomToggleButton
                active={refresh.turned}
                setActive={val => {
                  syncpresentdate();
                  setRefresh({ ...refresh, turned: val });
                }}
              />
            ) : null}
          </div>
        </div>
      )}

      {!loaded ? (
        <div className="flex justify-content-center p-mb-4">
          <ProgressSpinner style={{ width: "20rem", height: "20rem" }} strokeWidth="3" animationDuration="2s" />
        </div>
      ) : (
        props.children!({
          width: getChartSizeValue(getChartWidth()),
          height: getChartHeight(),
          data: traces || [],
          axesConfig: props.axesConfig ? props.axesConfig((traces as Partial<PlotDataEx>[]) || []) : undefined,
          chartConfig: config as Partial<Plotly.Config>,
          chartDialogOpen: chartDialog,
        })
      )}
      {chartDialog && (
        <Dialog
          visible={chartDialog}
          style={{ width: "min(100vw, 1000px)", height: "600px" }}
          modal={false}
          resizable
          draggable
          maximizable
          onHide={chartDialogHandler}
        >
          <ChartWrapper {...props} dynamicHeight chartDialogOpen />
        </Dialog>
      )}
    </div>
  );
}
